import type { ShownStatement, StatementLayout } from "../../../../knowledgeData";
import type { PlacedStatement } from "../regestParts";

/** A line concluded from others (Daniel's *erschlossen*), and the lines it rests on. */
export interface Conclusion {
  readonly line: ShownStatement<PlacedStatement>;
  /** The keys (`shown.key`) of the lines it rests on, in reading order. */
  readonly premises: readonly string[];
}

/** How a project's statements name what they are concluded from (C-B7 AB-5): the profile's `conclusions`. */
export interface ConclusionShape {
  readonly premises: string;
}

/**
 * The lines that are conclusions (Daniel's regest viewer, `render_vita`: an inference rests on other
 * statements): one whose said content has a row of `shape.premises` naming something another line
 * describes, which is then its premise. A row naming only what no line says, such as the source text
 * a statement is drawn from, makes no conclusion. Nothing without the profile's `conclusions`.
 */
export function conclusionsOf(lines: readonly ShownStatement<PlacedStatement>[], shape: ConclusionShape | null): Conclusion[] {
  if (shape === null) return [];
  return lines.flatMap((line) => {
    const named = new Set(premiseRows(line.layout, shape).flatMap((row) => row.values.flatMap((value) => (value.kind === "individual" ? [value.individual.iri] : []))));
    const premises = lines.filter((other) => other !== line && other.layout.said.some((description) => description.rows.length > 0 && named.has(description.individual.iri)));
    return premises.length === 0 ? [] : [{ line, premises: premises.map((premise) => premise.shown.key) }];
  });
}

/**
 * A conclusion's line as its section shows it: its premises are named there as the statements they
 * are ("Based on #1 Provision"), so the row that names them by their own individuals is not shown
 * a second time. Everything else it says stays.
 */
export function withoutPremiseRows(line: ShownStatement<PlacedStatement>, shape: ConclusionShape): ShownStatement<PlacedStatement> {
  const said = line.layout.said.map((description) => ({ ...description, rows: description.rows.filter((row) => row.property !== shape.premises) }));
  return { ...line, layout: { ...line.layout, said } };
}

function premiseRows(layout: StatementLayout, shape: ConclusionShape) {
  return layout.said.flatMap((description) => description.rows.filter((row) => row.property === shape.premises));
}
